import { getFunctions, httpsCallable } from 'firebase/functions';
import { onAuthStateChanged } from 'firebase/auth';
import { app, auth } from '../services/firebase';

/**
 * Aggregate operational counts, independent of Google Analytics consent.
 * Only allowlisted bucket names are sent: no client, match, device, session,
 * IP, page URL or player identifiers are in the metric payload.
 */
export type OperationalMetricKind =
  | `cpu_start_${'easy' | 'medium' | 'hard'}`
  | `cpu_complete_${'win' | 'loss' | 'draw'}`
  | 'online_participation_start'
  | `online_participation_complete_${'win' | 'loss' | 'draw'}`;

const previouslyCounted = new Set<string>();

async function waitForGameAuthentication(): Promise<boolean> {
  await auth.authStateReady();
  if (auth.currentUser) return true;

  // On the first visit, the app signs the guest in anonymously in a React
  // effect. A quick first game must not silently miss the aggregate metric.
  return new Promise<boolean>((resolve) => {
    let unsubscribe = () => {};
    const timeout = window.setTimeout(() => {
      unsubscribe();
      resolve(false);
    }, 10_000);
    unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) return;
      window.clearTimeout(timeout);
      unsubscribe();
      resolve(true);
    }, () => {
      window.clearTimeout(timeout);
      unsubscribe();
      resolve(false);
    });
  });
}

export function recordOperationalMetric(kind: OperationalMetricKind): void {
  // The gameplay does not wait for telemetry, and a missing connection is
  // harmless. The function only writes an anonymous aggregate counter.
  void waitForGameAuthentication().then(async (authenticated) => {
    if (!authenticated) return;
    const callable = httpsCallable<{ kind: OperationalMetricKind }, { recorded: boolean }>(
      getFunctions(app, 'europe-west1'),
      'recordOperationalMetric',
      { timeout: 10_000 },
    );
    await callable({ kind });
  }).catch(() => {
    // A network failure must never prevent the user from playing.
  });
}

/** Dedupe multiplayer participation locally; the key never leaves the tab. */
export function recordOperationalMetricOnce(kind: OperationalMetricKind, localKey: string): void {
  if (previouslyCounted.has(localKey)) return;
  previouslyCounted.add(localKey);
  recordOperationalMetric(kind);
}
